
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import Modal from '@/components/shared/Modal.vue';
import InputModalContainer from '@/components/shared/InputModalContainer.vue';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';
import CreditsInfo from '@/components/shared/CreditsInfo.vue';
import CartTakeoutOptions from './CartTakeoutOptions.vue';
import CartTipCustomAmount from './CartTipCustomAmount.vue';
import CartDiscount from './CartDiscount.vue';
import CartVoucher from './CartVoucher.vue';
import CartPayWhatYouCan from './CartPayWhatYouCan.vue';
const namespace: string = 'cart';

@Component<CartPricing>({
	components: {
		Modal,
		InputModalContainer,
		CartTakeoutOptions,
		CartTipCustomAmount,
		CartDiscount,
		CartVoucher,
		ToggleSwitch,
		CreditsInfo,
		CartPayWhatYouCan
	}
})

export default class CartPricing extends Vue {
	@Prop({ type: String, required: true, default: '' }) private subtotal!: string;
	@Prop({ type: String, required: false, default: '' }) private subsidyDiscountAmount!: string;
	@Prop({ type: String, required: false, default: '' }) private promoDiscount!: string;
	@Prop({ type: String, required: false, default: '' }) private memberDiscount!: string;
	@Prop({ type: String, required: true, default: '' }) private taxes!: string;
	@Prop({ type: String, required: false, default: '' }) private serviceCharge!: string;
	@Prop({ type: String, required: false, default: '' }) private deliveryCharge!: string;
	@Prop({ type: String, required: true, default: '' }) private tip!: string;
	@Prop({ type: String, required: false, default: '' }) private voucherAmount!: string;
	@Prop({ type: String, required: true, default: '' }) private total!: string;
	@Prop({ type: String, required: false, default: undefined }) private totalWithoutTaxes!: string | undefined;
	@Prop({ type: String, required: true, default: '' }) private totalWithTip!: string;
	@Prop({ type: Array, required: false, default: () => [] }) private taxInfo!: TaxInfo[];
	@Prop({ type: Object, required: true, default: () => {} }) private creditsApplied!: CreditsCosts;
	@Prop({ type: Boolean, required: false, default: false }) private canEditItems!: boolean;
	@Action('updateTip', { namespace }) private updateTip!: (tipObject: any) => void;
	@Action('setCosts', { namespace }) private setCosts!: (specifier?: string) => Promise<void>;
	@Action('setTipSelected', { namespace }) private setTipSelected!: (value: boolean) => void;
	@Action('applyCredits', { namespace }) private applyCredits!: (apply: boolean) => Promise<void>;
	@Action('setCreditsInitialized', { namespace }) private setCreditsInitialized!: (initialized: boolean) => void;
	@Action('toggleSubsidies', { namespace }) private toggleSubsidiesAction!: (enabled: boolean) => void;
	@Action('setChosenSubsidyDiscount', { namespace }) private setChosenSubsidyDiscountAction!: (chosenAmountToBePaid: number) => void;
	@Getter('getTipObject', { namespace }) private tipObject!: any;
	@Getter('getServiceChargeLabel', { namespace }) private serviceChargeLabel!: string;
	@Getter('getTipSelected', { namespace }) private tipSelected!: boolean;
	@Getter('isTakeOut', { namespace }) private isTakeOut!: boolean;
	@Getter('isPickupOnly', { namespace }) private isPickupOnly!: boolean;
	@Getter('isDeliveryOnly', { namespace }) private isDeliveryOnly!: boolean;
	@Getter('isDelivery', { namespace }) private isDelivery!: boolean | null | undefined;
	@Getter('cartContainsNonAlcoholicItems', { namespace }) private cartContainsNonAlcoholicItems!: boolean;
	@Getter('getMemberDiscountCode', { namespace }) private membershipDiscountCode!: string[];
	@Getter('getMemberProvider', { namespace }) private membershipProvider!: string | null;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;
	@Getter('getRestaurantDeliveryInfo', { namespace: 'restaurant' }) private restaurantDeliveryInfo!: DefaultDelivery | StadiumDelivery | CustomDelivery;
	@Getter('getRestaurantTipConfig', { namespace: 'restaurant' }) private tipConfig!: TipConfig;
	@Getter('getRestaurantVoucher', { namespace: 'restaurant' }) private restaurantVoucherInfo!: object;
	@Getter('getRestaurantDiscount', { namespace: 'restaurant' }) private hasDiscounts!: boolean;
	@Getter('showSplitTaxes', { namespace: 'restaurant' }) private showSplitTaxes!: boolean;
	@Getter('genericCateringShowPickupOption', { namespace: 'restaurant' }) private genericCateringShowPickupOption!: boolean;
	@Getter('isMarketplaceHub', { namespace: 'restaurant' }) private isMarketplaceHub!: boolean;
	@Getter('isK12Location', { namespace: 'restaurant' }) private isK12Location!: boolean;
	@Getter('isTaxInclusive', { namespace: 'restaurant' }) private isTaxInclusive!: boolean;
	@Getter('isTaxInclusiveWithInfo', { namespace: 'restaurant' }) private isTaxInclusiveWithInfo!: boolean;
	@Getter('isSubsidyPayWhatYouCan', { namespace: 'restaurant' }) private isSubsidyPayWhatYouCan!: boolean;
	@Getter('hidePricing', { namespace: 'restaurant' }) private hidePricing!: boolean;
	@Getter('isEventDayOrdering', { namespace: 'suites' }) private isEventDayOrdering!: boolean;
	@Getter('applySubsidies', { namespace }) private applySubsidies!: boolean;
	@Getter('haveCreditsBeenInitialized', { namespace }) private haveCreditsBeenInitialized!: boolean;
	@Getter('getUserCredits', { namespace: 'auth' }) private credits!: CreditsUserInfo;

	private tipValues: number[] = [15, 18, 20];
	private tipValueSelected: number = 2;
	private tipTitle: string|null = null;
	private inputTip: number = 0;
	private inputType: string = '';
	private opened: boolean = false;
	private settingCosts: boolean = false;
	private hideTipSelection: boolean = false;
	private forceTips: boolean = false;
	private noTips: boolean = false;
	private useCredits: boolean = false;
	private savedSubsidyValue: number | null = null;

	private get showTakeoutOptions(): boolean {
		return this.canEditItems &&
			(this.isTakeOut || this.genericCateringShowPickupOption) &&
			!!this.restaurantDeliveryInfo &&
			!!this.restaurantDeliveryInfo.type &&
			(!this.isPickupOnly && !this.isDeliveryOnly);
	}

	private get isLocationInQuebec(): boolean {
		return !!this.restaurant.address?.match(/, (QC|Québec|Quebec)/);
	}

	private get hideTipPercentage(): boolean {
		return this.isTaxInclusive && this.isLocationInQuebec;
	}

	/**
	* Tax amounts are displayed unless the prices are tax inclusive without
	* the tax breakdown, in which case only the 'Included' label is shown
	*
	* @return {boolean}
	*/
	private get showTaxValues(): boolean {
		return !this.isTaxInclusive || this.isTaxInclusiveWithInfo;
	}

	private get hasSubsidyOptOut(): boolean {
		return this.restaurant.subsidies?.[0]?.opt_out_enabled || false;
	}

	private get isDeliveryFeeCalculatedAtCheckout(): boolean {
		const info = this.restaurantDeliveryInfo as DefaultDelivery;
		return !!this.isDelivery &&
			!Number(this.deliveryCharge) &&
			info.delivery_fee_type === 'custom' &&
			!!info.delivery_fee_ranges?.length;
	}

	private get localizedTaxes(): TaxInfo[] {
		return this.taxInfo.map(tax => {
			// Adding ' ,' before QC and Quebec when checking the address to avoid false positives
			// Every address in the prod db has a comma before the province/state (from what I've seen)
			if (tax.name === 'PST' && this.isLocationInQuebec) {
				return {
					name: this.$t('checkout.cart.pricing.taxes.qst'),
					value: tax.value
				};
			}
			return {
				// If a translation for the tax doesn't exist fall back to the original name
				name: this.$te(`checkout.cart.pricing.taxes.${tax.name.toLowerCase()}`) ? this.$t(`checkout.cart.pricing.taxes.${tax.name.toLowerCase()}`) : tax.name,
				value: tax.value
			};
		});
	}

	/**
	* Set up tip values if any provided by the restaurant config
	*
	* @return {void}
	*/
	private created(): void {
		if(this.tipConfig) {
			this.tipValueSelected = this.tipConfig.default_value_selected ? this.tipConfig.default_value_selected : 2;
			this.tipValues = this.tipConfig.default_values ? this.tipConfig.default_values : [15, 18, 20];
			this.tipTitle = this.tipConfig.total_summary_title;
		}

		// Initialize credits toggle based on whether credits have been initialized for this checkout session
		if (this.credits && this.credits.balance > 0) {
			if (!this.haveCreditsBeenInitialized) {
				// First time opening cart in this checkout session - default to ON
				this.useCredits = true;
				this.toggleCredits();
			}
			else {
				// Credits already initialized for this session - respect the current state from store
				this.useCredits = this.creditsApplied.id !== '';
			}
		}
	}

	/**
	* Set up the initial tip value if none was clicked
	* and activate the right tip section
	*
	* @return {void}
	*/
	private async mounted(): Promise<void> {
		this.checkToHideOrForceTips();
		if(this.canEditItems) {
			this.selectRespectiveTipBox();
			if(this.tipValueSelected !== 4) {
				this.setTip({ tip: this.tipValues[this.tipValueSelected - 1], type: 'percentage' }, this.tipValueSelected, true);
			}

			await this.getCosts('cartOpened');
		}
	}

	/**
	 * We first check if all the values are equal, if they are it can mean two things:
	 * 1. All tip values are set to 0 so they are disabled by the restaurants,
	 * this means the restaurant do not want to accept tips and we will be removing
	 * the tip section altogether.
	 * 2. All tip values are set to a certain amount to be enforced by the user. This means
	 * the users cannot edit their selection and are forced to tip with that value.
	 * (IE: Service Charge gratuity)
	 *
	 * @return {void}
	 */
	private checkToHideOrForceTips(): void {
		if(this.tipValues.every(tipValue => tipValue === this.tipValues[0])) {
			this.hideTipSelection = true;
			if(this.tipValues.every( tipValue => tipValue === 0 )) {
				this.noTips = true;
			}
			else {
				this.forceTips = true;
				this.setTip({ tip: this.tipValues[0], type: 'percentage' }, 1);
			}
		}
	}

	/**
	* Select respective box depending on the tip
	* selected
	*
	* @return void
	*/
	// TODO: REFACTOR THIS INTO SUB COMPONENT
	private selectRespectiveTipBox(): void {
		if(this.tipSelected) {
			this.inputType = this.tipObject.type;

			// Display the percentage value and select the respectice box
			if(this.tipObject.type === 'percentage') {
				this.inputTip = this.tipObject.percentageValue;
				if(this.tipObject.percentageValue === this.tipValues[0]) {
					this.tipValueSelected = 1;
				}
				else if(this.tipObject.percentageValue === this.tipValues[1]) {
					this.tipValueSelected = 2;
				}
				else if(this.tipObject.percentageValue === this.tipValues[2]) {
					this.tipValueSelected = 3;
				}
				else {
					this.tipValueSelected = 4;
				}
			}

			// Display dollar amount and select the custom tip box
			else {
				this.inputTip = this.tipObject.amount.toFixed(2);
				this.tipValueSelected = 4;
			}
		}
	}

	/**
	* Calculate the tip value
	* * Quebec tip calculation now needs to happen without taxes
	*
	* @param {number]} tip
	* @return {string}
	*/
	private calculateTipPercentage(tip: number): string {
		if(this.isLocationInQuebec && typeof this.totalWithoutTaxes !== 'undefined') {
			return (+this.totalWithoutTaxes * (tip / 100)).toFixed(2);
		}
		return (+this.total * (tip / 100)).toFixed(2);
	}

	/**
	* Update the tip value in the store depending on which
	* tip box was selected
	*
	* @param {any} tipObject - value & type
	* @param {number} number
	* @return {void}
	*/
	private async setTip(tipObject: TipSelectionObject, id: number, onMounted?: boolean): Promise<void> {
		if(!this.tipSelected) {
			this.setTipSelected(true);
		}
		this.tipValueSelected = id;
		await this.updateTip(tipObject);

		// We are already getting the costs on mounted so no need to do it twice
		if(!onMounted) {
			await this.getCosts('updateTip');
		}
	}

	/**
	 * Get order's costs from the DB
	 * * It is public as it is being used by the CartViewer component
	 *
	 * @param {string} [specifier]
	 * @return {Promise<void>}
	 */
	public async getCosts(specifier?: string): Promise<void> {
		try {
			this.settingCosts = true;
			await this.setCosts(specifier);
		}
		catch(error) {
			if(typeof error === 'string') {
				this.$toasted.show(error, { type: 'error', position: 'top-center' }).goAway(5000);
			}
			else {
				this.$toasted.show(this.$t('checkout.cart.pricing.error_totals'), { type: 'error', position: 'top-center' }).goAway(5000);
			}
		}
		finally {
			this.settingCosts = false;
		}
	}

	/**
	* Set custom tip value depending on what type was selected
	*
	* @param {any} tipCustomObject - value & type
	*/
	private setCustomTip(tipCustomObject: any): void {
		this.inputType = tipCustomObject.type;
		this.inputTip = this.inputType === 'percentage' ? tipCustomObject.tip : tipCustomObject.tip.toFixed(2);
		this.setTip(tipCustomObject, 4);
	}

	/**
	 * Toggle the use of credits and trigger a costs calculation.
	 *
	 * @return {void}
	 */
	private toggleCredits(): void {
		this.applyCredits(this.useCredits);
		this.setCreditsInitialized(true);
		this.getCosts();
	}

	/**
	 * Handle subsidy toggle change and trigger a costs calculation.
	 *
	 * @param {boolean} enabled - new toggle state
	 * @return {void}
	 */
	private async onSubsidyToggleChange(enabled: boolean): Promise<void> {
		// When turning off, save the current slider value (amount user chose to pay)
		if (!enabled && this.isSubsidyPayWhatYouCan && this.subsidyDiscountAmount) {
			// The slider value is the amount the user chose to pay (subtotal - discount)
			const subtotalAmount = +this.subtotal;
			const discountAmount = +this.subsidyDiscountAmount;
			this.savedSubsidyValue = subtotalAmount - discountAmount;
		}

		this.toggleSubsidiesAction(enabled);

		// When turning back on, restore the saved slider value BEFORE getCosts
		if (enabled && this.savedSubsidyValue && this.isSubsidyPayWhatYouCan) {
			this.setChosenSubsidyDiscountAction(this.savedSubsidyValue);
			this.savedSubsidyValue = null; // Clear the saved value
		}

		await this.getCosts('updateSubsidyToggle');
	}

	/**
	* Hide the input modal
	*
	* @return {void}
	*/
	private hideInputModal(): void {
		this.opened = false;
	}
}
